// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { deleteUsers } from "../../api";

export const useDeleteClinicUsers = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (userEmails: Array<string>) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return deleteUsers(token, clinicId, userEmails);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate users query cache
    onSuccess: (_, userEmails) => {
      queryClient.invalidateQueries({ queryKey: ["clinic-users", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("User deleted successfully!", { count: userEmails.length }),
      });
    },
    onError: (_, userEmails) => {
      setSnackbarInfo({
        variant: "error",
        message: t("User could not be deleted!", { count: userEmails.length }),
      });
    },
  });
};
